import { isEqual } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';

import Tab from '@mui/material/Tab';
import Card from '@mui/material/Card';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import { alpha } from '@mui/material/styles';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import { Button, ListItemText, Stack } from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { TAB_QUERY } from 'src/utils/common-types';
import { hasPermission } from 'src/utils/has-permissions';
import { fCurrencyRupees } from 'src/utils/format-number';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import {
  deleteCheque,
  getChequeReceived,
  getCheques,
  getChequeTotal,
  removeChequeItem,
} from 'src/redux/slices/cheque';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import {
  CHEQUE_STATUS_OPTIONS,
  ChequeStatus,
  IChequeListItem,
  IChequeReceived,
  IChequeTableFilters,
  IChequeTableFilterValue,
  IChequeTableSort,
} from 'src/types/cheque';

import ChequeTableRow from '../cheque-table-row';
import ChequeTableToolbar from '../cheque-table-toolbar';

const STATUS_OPTIONS = [{ value: 'all', label: 'All' }, ...CHEQUE_STATUS_OPTIONS];

const TABLE_HEAD = [
  { id: 'chequeNumber', label: 'Cheque Number', align: 'left', primary: true },
  { id: 'customer', label: 'Customer', align: 'left', primary: true },
  { id: 'holder', label: 'Holder', align: 'left', primary: true },
  { id: 'chequeDate', label: 'Cheque Date', align: 'left', primary: true },
  { id: 'amount', label: 'Amount', align: 'right', primary: true },
  { id: 'status', label: 'Status', align: 'left', primary: true },
  { id: 'receivedDate', label: 'Received At', align: 'left', primary: true },
  { id: '', primary: true },
];

function getDynamicTableHead(filters: IChequeTableFilters) {
  // Create a copy to avoid mutating the original array
  const updatedTableHead = [...TABLE_HEAD];

  if (filters.paidDate) {
    const statusIndex = updatedTableHead.findIndex((col) => col.id === 'status');
    if (statusIndex !== -1) {
      updatedTableHead.splice(statusIndex + 1, 0, {
        id: 'paidTo',
        label: 'Paid To',
        align: 'left',
        primary: true,
      });
    }
  }

  return updatedTableHead;
}

const defaultFilters: IChequeTableFilters = {
  generalSearch: '',
  bank: null,
  chequeDate: null,
  receivedVia: null,
  startDate: null,
  endDate: null,
  receivedDate: null,
  paidDate: null,
  type: null,
};

const defaultSortBy: IChequeTableSort = {
  sortBy: 'receivedDate',
  order: 'desc',
};

export default function ChequeListView() {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentTab = searchParams.get(TAB_QUERY);

  const { status, cheque, error, total, received } = useAppSelector((state) => state.cheque);

  const chequeTotal: { totalAmount: number | null } = total.data;

  const receivedPeople: IChequeReceived[] = received.data;

  const { user: authUser } = useAuthContext();

  const table = useTable({
    defaultRowsPerPage: 25,
    defaultDense: true,
  });

  const dispatch = useAppDispatcher();

  const { page, rowsPerPage } = table;

  const settings = useSettingsContext();

  const denseHeight = table.dense ? 60 : 80;

  const router = useRouter();

  const [filters, setFilters] = useState(defaultFilters);

  const [sort, setSort] = useState(defaultSortBy);

  const canReset = !isEqual(defaultFilters, filters);

  const [statusTab, setStatusTab] = useState<string>(currentTab || ChequeStatus.IN_HAND);

  const handleFilters = useCallback(
    (name: string, value: IChequeTableFilterValue) => {
      table.onResetPage();

      setFilters((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    [table]
  );

  const handleEditRow = useCallback(
    (id: string) => {
      router.push(paths.dashboard.cheque.edit(id));
    },
    [router]
  );

  const handleFilterStatus = useCallback(
    (event: React.SyntheticEvent, newValue: string) => {
      setStatusTab(newValue);

      setSearchParams({ [TAB_QUERY]: newValue });
    },
    [setSearchParams]
  );

  const handleResetFilters = useCallback(() => {
    setSearchParams({ [TAB_QUERY]: 'all' });
    setFilters(defaultFilters);
  }, [setSearchParams]);

  const fetchPaginatedCheques = useCallback(() => {
    dispatch(
      getChequeTotal({
        generalSearch: filters.generalSearch,
        status: statusTab === 'all' ? null : statusTab,
        startDate: filters.startDate,
        endDate: filters.endDate,
        receivedDate: filters.receivedDate,
        receivedVia: filters.receivedVia,
        paidDate: filters.paidDate,
        type: filters.type,
      })
    );

    dispatch(
      getCheques({
        page,
        limit: rowsPerPage,
        sortBy: sort.sortBy,
        sort: sort.order,
        generalSearch: filters.generalSearch,
        status: statusTab === 'all' ? null : statusTab,
        startDate: filters.startDate,
        endDate: filters.endDate,
        receivedDate: filters.receivedDate,
        receivedVia: filters.receivedVia,
        paidDate: filters.paidDate,
        type: filters.type,
      })
    );
  }, [dispatch, page, rowsPerPage, sort, filters, statusTab]);

  const fetchChequeReceived = useCallback(() => {
    dispatch(getChequeReceived());
  }, [dispatch]);

  const handleDeleteRow = useCallback(
    async (item: IChequeListItem, index: number) => {
      dispatch(removeChequeItem({ itemId: item._id }));

      try {
        await dispatch(deleteCheque({ item, index })).unwrap();

        fetchPaginatedCheques();
        enqueueSnackbar('Cheque deleted');
      } catch (e) {
        if (typeof e === 'string') {
          enqueueSnackbar(e, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(e?.message, {
            variant: 'error',
          });
        }
      }
    },
    [dispatch, fetchPaginatedCheques]
  );

  useEffect(() => {
    fetchPaginatedCheques();
    fetchChequeReceived();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPaginatedCheques, fetchChequeReceived]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Cheques"
        links={[]}
        action={
          <Stack direction="row" gap={2}>
            {/* {hasPermission(
              authUser,
              'any',
              AuthPermissions.DOWNLOAD_CHEQUE_REPORT,
              AuthPermissions.ADMIN_PERMISSION,
              AuthPermissions.SUPER_ADMIN_PERMISSION
            ) && (
              <Tooltip title="Download Cheque Report">
                <Button variant="outlined" color="primary" onClick={downloadChequeReport}>
                  <Iconify icon="eva:arrow-circle-down-fill" width={24} />
                </Button>
              </Tooltip>
            )} */}

            <ListItemText
              primary="Total Value"
              secondary={fCurrencyRupees(chequeTotal?.totalAmount ?? 0)}
              primaryTypographyProps={{ typography: 'body2' }}
              secondaryTypographyProps={{
                component: 'span',
                color: 'text.disabled',
              }}
            />

            {hasPermission(
              authUser,
              'any',
              AuthPermissions.CREATE_CHEQUE,
              AuthPermissions.ADMIN_PERMISSION,
              AuthPermissions.SUPER_ADMIN_PERMISSION
            ) && (
              <Button
                component={RouterLink}
                href={paths.dashboard.cheque.new}
                variant="contained"
                startIcon={<Iconify icon="mingcute:add-line" />}
              >
                New Cheque
              </Button>
            )}
          </Stack>
        }
        sx={{
          mb: { xs: 3 },
        }}
      />

      <Card>
        <Tabs
          value={statusTab}
          onChange={handleFilterStatus}
          sx={{
            px: 2.5,
            boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
          }}
        >
          {STATUS_OPTIONS.map((tab) => (
            <Tab key={tab.value} iconPosition="end" value={tab.value} label={tab.label} />
          ))}
        </Tabs>

        <ChequeTableToolbar
          filters={filters}
          onFilters={handleFilters}
          handleResetFilters={handleResetFilters}
          canReset={canReset}
          received={receivedPeople}
          tableStatus={statusTab}
        />

        <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '450px' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
              <TableHeadCustom
                order={sort.order}
                orderBy={sort.sortBy}
                headLabel={getDynamicTableHead(filters).filter(({ primary }) => {
                  if (settings.themeLayout === 'vertical') {
                    return primary;
                  }
                  return true;
                })}
                rowCount={cheque.items.length}
                numSelected={table.selected.length}
                onSort={(id) => {
                  if (id === sort.sortBy) {
                    setSort((prev) => ({
                      ...prev,
                      order: prev.order === 'asc' ? 'desc' : 'asc',
                    }));
                  } else {
                    setSort({
                      sortBy: id,
                      order: 'asc',
                    });
                  }
                }}
              />

              <TableBody>
                {status === 'loading' ? (
                  [...Array(table.rowsPerPage)].map((i, index) => (
                    <TableSkeleton key={index} sx={{ height: denseHeight }} />
                  ))
                ) : (
                  <>
                    {cheque?.items.map((row: IChequeListItem, index: number) => (
                      <ChequeTableRow
                        key={row._id}
                        row={row}
                        selected={table.selected.includes(row._id)}
                        onDeleteRow={() => handleDeleteRow(row, index)}
                        onEditRow={() => handleEditRow(row._id)}
                        hasPaidDateFilter={!!filters.paidDate}
                      />
                    ))}
                  </>
                )}

                {status !== 'failure' && cheque.count === 0 && (
                  <TableNoData message="No cheques found" notFound={cheque.count === 0} />
                )}

                {status === 'failure' && <TableErrorData notFound error={error} />}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          dense
          count={cheque.count}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          onChangeDense={table.onChangeDense}
        />
      </Card>
    </Container>
  );
}
